import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listChats, deleteChat, updateChat } from "../api/chats";
import { useToast } from "../context/ToastContext";

const GROUP_ORDER = ["Today", "Yesterday", "Last 7 Days", "Older"];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const utc = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
  const diff = Date.now() - new Date(utc).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

function RenameInput({ chatId, currentTitle, onDone }) {
  const [value, setValue] = useState(currentTitle);
  const inputRef = useRef(null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const renameMut = useMutation({
    mutationFn: (title) => updateChat(chatId, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      onDone();
    },
    onError: () => {
      toast("Rename failed", "error");
      onDone();
    },
  });

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === currentTitle) { onDone(); return; }
    renameMut.mutate(trimmed);
  };

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); save(); }
        if (e.key === "Escape") onDone();
      }}
      onBlur={save}
      onClick={(e) => e.stopPropagation()}
      className="flex-1 bg-[#2B2B30] border border-[#444] rounded-md px-2 py-0.5 text-sm text-dark-text outline-none min-w-0"
    />
  );
}

export default function AllChatsPanel({ selectedChatId, onSelectChat, onNewChat, onClose }) {
  const [search, setSearch] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["chats"],
    queryFn: async () => {
      const res = await listChats();
      return res.data.data;
    },
  });

  const allChats = useMemo(
    () => GROUP_ORDER.flatMap((g) => data?.[g] || []),
    [data]
  );

  const filtered = allChats.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase())
  );

  const deleteMut = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) await deleteChat(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      setSelected(new Set());
      setSelectMode(false);
      toast("Deleted", "success");
    },
    onError: () => toast("Delete failed", "error"),
  });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.id)));
    }
  };

  const handleDelete = () => {
    if (!selected.size) return;
    deleteMut.mutate([...selected]);
  };

  return (
    <div className="flex flex-col h-full items-center">
      <div className="flex flex-col h-full w-full max-w-[80%] md:max-w-[75%]">
        {/* Header */}
        <div className="flex items-center justify-between pt-20 pb-5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <ion-icon name="chatbubbles-outline" style={{ fontSize: "20px", color: "#e5e5e5" }} />
            <span className="text-base font-semibold">All Chats</span>
          </div>
          <button
            onClick={onClose}
            className="text-dark-muted hover:text-dark-text transition-colors"
          >
            <ion-icon name="close-outline" style={{ fontSize: "22px" }} />
          </button>
        </div>

        {/* Action bar */}
        <div className="mb-5 flex-shrink-0">
          {selectMode ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-dark-muted mr-1">
                {selected.size} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="rounded-lg border border-[#3a3a3a] px-4 py-1.5 text-sm font-medium text-dark-text hover:border-[#555] transition-colors"
              >
                Select all
              </button>
              <button
                onClick={handleDelete}
                disabled={!selected.size || deleteMut.isPending}
                className="rounded-lg border border-[#3a3a3a] px-4 py-1.5 text-sm font-medium text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-40"
              >
                Delete
              </button>
              <button
                onClick={() => { setSelectMode(false); setSelected(new Set()); }}
                className="text-sm text-dark-muted hover:text-dark-text transition-colors ml-1"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setSelectMode(true)}
                className="rounded-lg border border-[#3a3a3a] px-4 py-1.5 text-sm font-medium text-dark-text hover:border-[#555] transition-colors"
              >
                Select chats
              </button>
              <button
                onClick={onNewChat}
                className="rounded-lg bg-white px-4 py-1.5 text-sm font-medium text-dark-bg hover:bg-[#f5f5f5] transition-colors"
              >
                New chat
              </button>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-2 flex-shrink-0">
          <div className="flex items-center gap-3 rounded-xl bg-[#2B2B30] px-4 py-2.5">
            <ion-icon name="search-outline" style={{ fontSize: "16px", color: "#555" }} />
            <input
              type="text"
              placeholder="Search chats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-dark-text placeholder-[#555] outline-none"
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto pt-2">
          {filtered.length === 0 && (
            <p className="py-8 text-center text-sm text-dark-muted">No chats found</p>
          )}
          {filtered.map((chat) => {
            const isSelected = selected.has(chat.id);
            const isEditing = editingId === chat.id;
            const isHovered = hoveredId === chat.id;

            return (
              <div
                key={chat.id}
                onClick={() => {
                  if (isEditing) return;
                  if (selectMode) toggleSelect(chat.id);
                  else onSelectChat(chat.id);
                }}
                onMouseEnter={() => setHoveredId(chat.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="flex items-center gap-4 py-3.5 cursor-pointer hover:bg-[#2B2B30] rounded-2xl px-4 transition-colors"
              >
                {selectMode && (
                  <div
                    className={`h-5 w-5 flex-shrink-0 flex items-center justify-center rounded transition-colors ${
                      isSelected
                        ? "bg-accent border border-accent"
                        : "border border-[#444]"
                    }`}
                  >
                    {isSelected && (
                      <ion-icon name="checkmark-outline" style={{ fontSize: "13px", color: "#fff" }} />
                    )}
                  </div>
                )}

                <div className="flex flex-1 items-center gap-3 min-w-0">
                  {isEditing ? (
                    <RenameInput
                      chatId={chat.id}
                      currentTitle={chat.title}
                      onDone={() => setEditingId(null)}
                    />
                  ) : (
                    <>
                      <span className="font-semibold text-sm text-dark-text truncate flex-1">
                        {chat.title}
                      </span>
                      <span className="text-xs text-dark-muted flex-shrink-0">
                        {timeAgo(chat.created_at)}
                      </span>
                      {!selectMode && isHovered && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(chat.id);
                          }}
                          className="flex-shrink-0 text-[#555] hover:text-[#aaa] transition-colors ml-1"
                          title="Rename"
                        >
                          <ion-icon name="pencil-outline" style={{ fontSize: "14px" }} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
